/**
 * /transport/international — Public directory of approved international
 * couriers. SEO-friendly (no auth required) — a buyer searching
 * "import French Bulldog from Romania" should land here.
 *
 * Pulls from `GET /api/transport/international/directory` with
 * optional `?origin=` and `?destination=` filters.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { AirplaneTiltIcon as AirplaneTilt, ShieldCheckIcon as ShieldCheck, GlobeIcon as Globe, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";

export default function InternationalTransportDirectory() {
  const [transporters, setTransporters] = useState([]);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: "60" });
      if (origin.trim()) p.set("origin", origin.trim());
      if (destination.trim()) p.set("destination", destination.trim());
      const r = await api.get(`/transport/international/directory?${p.toString()}`);
      setTransporters(r.data.transporters || []);
    } finally {
      setLoading(false);
    }
  }, [origin, destination]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  return (
    <AppShell>
      <div
        className="max-w-6xl mx-auto px-4 py-10"
        data-testid="intl-transport-directory"
      >
        {/* ============= Hero ============= */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-2">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[3px] text-[#8A6A00] font-bold uppercase mb-3">
              International Corridor Transport
            </div>
            <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
              <AirplaneTilt
                size={36}
                weight="duotone"
                className="text-ink shrink-0"
              />
              Find an international courier
            </h1>
            <p className="text-[14px] text-[#5C5C5C] mt-3 leading-relaxed">
              Independent, professional pet couriers who fly dogs across
              borders. Use them to import a puppy from Europe, return
              with breeding stock from Asia, or ship a placement abroad.
              Armstrong introduces you &mdash; you arrange the trip directly
              with the courier.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Link to="/transport/international/apply">
              <Button
                variant="outline"
                className="rounded-full text-[12px] h-9 px-4"
                data-testid="intl-cta-apply"
              >
                I&rsquo;m a courier &mdash; apply →
              </Button>
            </Link>
          </div>
        </div>

        {/* ============= Filters ============= */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
          <Input
            placeholder="Origin country (e.g. Romania)"
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            className="rounded-full h-10"
            data-testid="intl-filter-origin"
          />
          <Input
            placeholder="Destination country / city (e.g. USA, JFK)"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            className="rounded-full h-10"
            data-testid="intl-filter-destination"
          />
        </div>

        {/* ============= Results ============= */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-44 rounded-xl" />
            ))
          ) : transporters.length === 0 ? (
            <EmptyState />
          ) : (
            transporters.map((t) => <CourierCard key={t.id} t={t} />)
          )}
        </div>

        {/* ============= Footer disclaimer (venue posture) ============= */}
        <div className="mt-12 border-t border-[#E5D08A] pt-6 text-[11px] text-[#8B8B8B] leading-relaxed max-w-3xl">
          <strong>Important:</strong> Armstrong K9 Registry introduces
          buyers to independent international couriers. We do not
          import, export, broker, or ship animals. We do not verify
          CDC, USDA APHIS, IATA, or origin-country documentation. All
          money, customs, brokerage, and carriage are between you and
          the courier. Use these introductions at your own risk and
          confirm credentials and pricing before paying anything.
        </div>
      </div>
    </AppShell>
  );
}

function CourierCard({ t }) {
  const corridors = (t.corridors || []).slice(0, 3);
  return (
    <Link
      to={`/transport/international/${t.slug}`}
      className="group rounded-xl border border-[#E5D08A] bg-white p-5 hover:shadow-md transition-shadow"
      data-testid={`intl-courier-card-${t.slug}`}
    >
      <div className="flex items-start gap-4">
        {t.photo_url ? (
          <img
            src={t.photo_url}
            alt={t.display_name}
            className="w-14 h-14 rounded-full object-cover border border-[#F0EAD6]"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-[#FBF7EC] border border-[#F0EAD6] flex items-center justify-center">
            <Globe size={28} weight="duotone" className="text-[#8A6A00]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-spectral text-xl tracking-tight truncate">
              {t.display_name}
            </h3>
            {t.verified_badge_at && (
              <span className="inline-flex items-center gap-1 text-[10px] text-[#8A6A00] font-semibold bg-[#FBF7EC] border border-[#E5D08A] px-2 py-0.5 rounded-full">
                <ShieldCheck size={12} weight="duotone" /> Verified
              </span>
            )}
          </div>
          <div className="text-[12px] text-[#6B6B6B] mt-0.5">
            Based in <strong>{t.home_country}</strong>
            {t.years_in_business > 0 && (
              <> · {t.years_in_business} yrs in business</>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {corridors.map((c) => (
              <span
                key={c}
                className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBF7EC] border border-[#F0EAD6] text-ink"
              >
                {c}
              </span>
            ))}
            {(t.corridors || []).length > 3 && (
              <span className="text-[11px] text-[#8B8B8B]">
                +{t.corridors.length - 3} more
              </span>
            )}
          </div>
          <div className="mt-3 flex items-center gap-3 text-[11px] text-[#8B8B8B]">
            {t.iata_lar_certified && (
              <span className="inline-flex items-center gap-1">
                <CheckCircle size={12} weight="fill" /> IATA LAR
              </span>
            )}
            {t.ipata_member_number_verified && (
              <span className="inline-flex items-center gap-1">
                <CheckCircle size={12} weight="fill" /> IPATA
              </span>
            )}
            {t.cdc_permit_verified && (
              <span className="inline-flex items-center gap-1">
                <CheckCircle size={12} weight="fill" /> CDC Permit
              </span>
            )}
          </div>
          <div className="mt-3 text-[12px] text-crimson group-hover:text-ink inline-flex items-center gap-1 font-semibold">
            View profile <ArrowRight size={12} />
          </div>
        </div>
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="col-span-full text-center py-16 border border-dashed border-[#E5D08A] rounded-xl bg-[#FBF7EC]">
      <Globe
        size={36}
        weight="duotone"
        className="text-[#8A6A00] mx-auto mb-3"
      />
      <h3 className="font-spectral text-xl mb-1">No couriers match</h3>
      <p className="text-[13px] text-[#6B6B6B] max-w-md mx-auto leading-relaxed">
        We&rsquo;re growing the international roster fast &mdash; adjust your
        origin / destination filters, or check back soon. If you&rsquo;re a
        courier who works this corridor, you can apply to be listed.
      </p>
      <Link to="/transport/international/apply">
        <Button
          variant="outline"
          className="rounded-full text-[12px] h-9 px-4 mt-5"
          data-testid="intl-empty-apply"
        >
          Become a verified courier
        </Button>
      </Link>
    </div>
  );
}
